import React, { useState, useEffect, Suspense } from 'react'

import { getUserAgent } from '@sainsburys-tech/boltui-utils'
import { Alert, ErrorBoundary, Spinner } from '@sainsburys-tech/bolt'
import loadable from '@loadable/component'

import AR3DButton from '../ArKit/AR3DButton'

import * as Styled from './ThreeViewer.styles'

const Viewer = loadable(() => import(/* webpackChunkName: "ThreeDModalViewer" */ './Viewer/ModelViewer'))

const Loading = () => <Spinner size={200} centered />

const ViewerContent = ({ data }) => {
  return (
    <div className='three_d_content'>
      <Suspense fallback={<Loading />}>
        <Viewer data={data} />
      </Suspense>
    </div>
  )
}

const isVideoCompatible = () => {
  // This is necesary because amplience is redirecting the video and safari and the rest don't support CORS Video attr
  const userAgent = getUserAgent()
  if (userAgent.name === 'firefox' || userAgent.name === 'chrome') {
    return true
  }
  return false
}

const hasVideo = modelData =>
  modelData.albedo && modelData.albedo.filter(texture => texture.mat === 'Video').map(video => video.link)[0]

const ErrorMessage = () => (
  <Alert kind='error' isCloseable={false} className='alert'>
    <div>Sorry, we can’t show the 3D viewer right now.</div>
  </Alert>
)

const ThreeViewerDrawer = ({ show, modelData, title, dataTest }) => {
  const videoPath = isVideoCompatible() && hasVideo(modelData)
  return (
    <Styled.Drawer
      position='right'
      show={show}
      heading={title}
      onClose={() => window.history.back()}
      padding={false}
      data-test={`${dataTest}-drawer`}
    >
      {videoPath && (
        // eslint-disable-next-line react/no-unknown-property
        <video id='video' crossOrigin='anonymous' style={{ display: 'none' }} loop playsinline>
          <source src={videoPath} type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
          <track kind='captions' />
        </video>
      )}
      <ErrorBoundary retry={1} FallbackComponent={<ErrorMessage />}>
        <ViewerContent data={modelData} />
      </ErrorBoundary>
    </Styled.Drawer>
  )
}

const ThreeViewer = ({ modelData, title, open, 'data-test': dataTest, thumbnail = false, isFullSize }) => {
  const [show, setShow] = useState(open || false)

  const handleBackButton = () => onClose() //eslint-disable-line
  const onClose = () => {
    setShow(false)
    window.removeEventListener('popstate', handleBackButton)
    if (window.location.hash) {
      window.history.back()
    }
  }

  const onOpen = () => {
    window.history.pushState({ is3dOpen: true }, null, window.location.pathname)
    window.addEventListener('popstate', handleBackButton)
    setShow(true)
  }

  useEffect(() => {
    if (open) {
      onOpen()
    }
  }, [open])

  if (!modelData) return null

  return (
    <ErrorBoundary retry={2}>
      <AR3DButton
        onClick={onOpen}
        text='View in 3D'
        data-test={dataTest}
        thumbnail={thumbnail}
        isFullSize={isFullSize}
      />
      <ThreeViewerDrawer {...{ show, modelData, title, dataTest }} />
    </ErrorBoundary>
  )
}

export default ThreeViewer
export { ThreeViewerDrawer, isVideoCompatible }
